import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { DateHierarchyFilter } from '../components/DateHierarchyFilter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Search, Plus, Pencil, Trash2, Download, Upload, FileSpreadsheet,
  ChevronDown, X, Paperclip, BarChart3, ChevronLeft, ChevronRight,
  MoreHorizontal, Eye, TrendingUp, Wallet, Receipt, CalendarDays, CheckSquare,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;
const COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#4f46e5', '#c026d3', '#d97706', '#059669'];

const fmtCurrency = (v) => {
  if (!v && v !== 0) return '';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    const [y, m, day] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${parseInt(day)} ${months[parseInt(m)-1]} ${y}`;
  } catch { return d; }
};

const TYPE_LABELS = { 'Petty Cash': 'Petty', 'Monthly Expense': 'Monthly', 'One-Time Expense': 'One-Time' };
const TYPE_COLORS = {
  'Petty Cash': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  'Monthly Expense': 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800',
  'One-Time Expense': 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800',
};

const ExpenseTypeBadge = ({ type }) => (
  <span className={`inline-block px-2 py-px rounded border text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap ${TYPE_COLORS[type] || 'bg-gray-50 text-gray-600 border-gray-200'}`} data-testid={`badge-${type}`}>
    {TYPE_LABELS[type] || 'Petty'}
  </span>
);

const STATUS_COLORS = {
  'Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Pending Approval': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Rejected': 'bg-red-50 text-red-700 border-red-200',
  'Draft': 'bg-gray-50 text-gray-500 border-gray-200',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-px rounded border text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap ${STATUS_COLORS[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
    {status === 'Pending Approval' ? 'PENDING' : (status || 'DRAFT').toUpperCase()}
  </span>
);

const MultiFilter = ({ label, options, selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`gap-1.5 h-8 text-xs ${selected.length ? 'border-primary text-primary' : ''}`}
          data-testid={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          {label}
          {selected.length > 0 && <Badge variant="secondary" className="ml-1 px-1 py-0 text-[10px] h-4 rounded-full">{selected.length}</Badge>}
          <ChevronDown className="w-3 h-3 ml-0.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="start">
        {options.length > 5 && <Input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="mb-2 h-7 text-xs" />}
        <div className="max-h-44 overflow-y-auto space-y-0.5">
          {filtered.length === 0 ? <p className="text-xs text-muted-foreground text-center py-2">No options</p> :
            filtered.map(o => (
              <label key={o} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent cursor-pointer text-xs">
                <Checkbox checked={selected.includes(o)} onCheckedChange={() => onToggle(o)} className="h-3.5 w-3.5" />
                <span className="truncate">{o}</span>
              </label>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Analytics sub-component
const AnalyticsView = ({ getAuthHeader }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [location, setLocation] = useState('');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (location) params.set('location', location);
      const res = await fetch(`${API}/expenses/analytics?${params}`, { headers: getAuthHeader() });
      if (res.ok) setAnalytics(await res.json());
    } catch { /* */ }
    finally { setLoading(false); }
  }, [getAuthHeader, dateFrom, dateTo, location]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  if (!analytics) return <p className="text-center text-muted-foreground py-8">No data</p>;

  return (
    <div className="space-y-6" data-testid="expense-analytics">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[140px] h-8 text-xs" /></div>
        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[140px] h-8 text-xs" /></div>
        <div className="space-y-1"><Label className="text-xs">Location</Label>
          <Select value={location} onValueChange={v => setLocation(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="All Locations" /></SelectTrigger>
            <SelectContent>{[<SelectItem key="_all" value="_all">All Locations</SelectItem>, ...(analytics.locations || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)]}</SelectContent>
          </Select>
        </div>
        {(dateFrom || dateTo || location) && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setDateFrom(''); setDateTo(''); setLocation(''); }}><X className="w-3 h-3 mr-1" />Clear</Button>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-slate-300 uppercase tracking-wider">Total Expenses</p>
          <p className="text-2xl font-bold mt-1" data-testid="analytics-total">{fmtCurrency(analytics.total_expenses)}</p>
          <p className="text-[11px] text-slate-400 mt-1">{analytics.total_records} records</p>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 text-white border-0"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-blue-100 uppercase tracking-wider">One-Time Expenses</p>
          <p className="text-2xl font-bold mt-1">{fmtCurrency(analytics.onetime_expense_total)}</p>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-orange-500 to-amber-500 text-white border-0"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-orange-100 uppercase tracking-wider">Petty Cash</p>
          <p className="text-2xl font-bold mt-1">{fmtCurrency(analytics.petty_cash_total)}</p>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-purple-600 to-violet-500 text-white border-0"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-purple-100 uppercase tracking-wider">Monthly Expenses</p>
          <p className="text-2xl font-bold mt-1">{fmtCurrency(analytics.monthly_expense_total)}</p>
        </CardContent></Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Expense Type</CardTitle></CardHeader><CardContent>
          {analytics.type_wise?.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={analytics.type_wise} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                {analytics.type_wise.map((_, i) => <Cell key={i} fill={['#2563eb', '#ea580c', '#7c3aed'][i] || COLORS[i]} />)}
              </Pie><Tooltip formatter={v => fmtCurrency(v)} /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm text-center py-8">No data</p>}
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Category</CardTitle></CardHeader><CardContent>
          {analytics.category_wise?.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.category_wise} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
                <Tooltip formatter={v => fmtCurrency(v)} /><Bar dataKey="amount" fill="#2563eb" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm text-center py-8">No data</p>}
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Location</CardTitle></CardHeader><CardContent>
          {analytics.location_wise?.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={analytics.location_wise} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {analytics.location_wise.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip formatter={v => fmtCurrency(v)} /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm text-center py-8">No data</p>}
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Trend</CardTitle></CardHeader><CardContent>
          {analytics.month_wise?.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.month_wise}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={v => fmtCurrency(v)} /><Legend /><Line type="monotone" dataKey="amount" name="Amount" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm text-center py-8">No data</p>}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default function DailyExpensesPage() {
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || 'Employee';
  const canWrite = ['Admin Head', 'Super Admin', 'Admin Team', 'IT Team', 'IT Head'].includes(role);
  const canDelete = role === 'Super Admin';
  const canViewRecords = ['Admin Head', 'Super Admin', 'Admin Team', 'Management', 'IT Team', 'IT Head'].includes(role);
  const canViewAnalytics = ['Admin Head', 'Super Admin', 'Admin Team', 'Management', 'IT Team', 'IT Head'].includes(role);
  const canApprove = ['Admin Head', 'Super Admin', 'Management'].includes(role);
  const hasAccess = canViewRecords || canViewAnalytics;

  const [activeTab, setActiveTab] = useSessionState('daily-expenses:activeTab', canViewRecords ? 'records' : 'analytics');
  const [expenses, setExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useSessionState('daily-expenses:page', 1);
  const [formOptions, setFormOptions] = useState({ categories: {}, subcategories_by_category: {}, vendors_by_category: {}, locations: [], all_locations: [], location_locked: false, default_location: '', entities: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);

  // Approval state
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [selectedPending, setSelectedPending] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [rejectDialog, setRejectDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bulkFileRef = useRef(null);

  // Filters (persisted to sessionStorage so they survive navigation)
  const [searchQuery, setSearchQuery] = useSessionState('daily-expenses:search', '');
  const [selLocations, setSelLocations] = useSessionState('daily-expenses:locations', []);
  const [selCategories, setSelCategories] = useSessionState('daily-expenses:categories', []);
  const [selSubcategories, setSelSubcategories] = useSessionState('daily-expenses:subcategories', []);
  const [selTypes, setSelTypes] = useSessionState('daily-expenses:types', []);
  const [dateFrom, setDateFrom] = useSessionState('daily-expenses:dateFrom', '');
  const [dateTo, setDateTo] = useSessionState('daily-expenses:dateTo', '');
  const [sortField, setSortField] = useSessionState('daily-expenses:sortField', 'date');
  const [sortDir, setSortDir] = useSessionState('daily-expenses:sortDir', 'desc');

  // Department slicer — Super Admin / Management can choose; IT/Admin roles auto-locked
  const forcedExpModule = ['IT Team', 'IT Head'].includes(role)
    ? 'IT'
    : ['Admin Team', 'Admin Head'].includes(role)
      ? 'Admin'
      : null;
  const [expModuleFilter, setExpModuleFilter] = useSessionState('daily-expenses:module', forcedExpModule || 'Admin');

  // KPI
  const [kpis, setKpis] = useState(null);

  // Form
  const emptyForm = { date: '', location: '', category: '', subcategory: '', amount: '', vendor_name: '', description: '', attachment: null, expense_type: 'Petty Cash', entity: '', payment_mode: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchFormOptions = useCallback(async () => {
    try {
      const effectiveMod = forcedExpModule || expModuleFilter || '';
      const url = effectiveMod ? `${API}/expenses/form-options?module=${effectiveMod}` : `${API}/expenses/form-options`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) setFormOptions(await res.json());
    } catch { /* */ }
  }, [getAuthHeader, forcedExpModule, expModuleFilter]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', PAGE_SIZE);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (selLocations.length === 1) params.set('location', selLocations[0]);
      if (selCategories.length === 1) params.set('category', selCategories[0]);
      if (selSubcategories.length === 1) params.set('subcategory', selSubcategories[0]);
      if (selTypes.length === 1) params.set('expense_type', selTypes[0]);
      if (searchQuery) params.set('search', searchQuery);
      // Department scope (Super Admin/Management can switch; others are backend-enforced)
      const effectiveMod = forcedExpModule || expModuleFilter;
      if (effectiveMod) params.set('module', effectiveMod);
      const res = await fetch(`${API}/expenses?${params}`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setExpenses(data.expenses || []);
        setTotalCount(data.total || 0);
      }
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  }, [getAuthHeader, page, dateFrom, dateTo, selLocations, selCategories, selSubcategories, selTypes, searchQuery, expModuleFilter, forcedExpModule]);

  const fetchKpis = useCallback(async () => {
    try {
      const res = await fetch(`${API}/expenses/analytics`, { headers: getAuthHeader() });
      if (res.ok) setKpis(await res.json());
    } catch { /* */ }
  }, [getAuthHeader]);

  const fetchPending = useCallback(async () => {
    if (!canApprove) return;
    try {
      const res = await fetch(`${API}/expenses/pending-approvals`, { headers: getAuthHeader() });
      if (res.ok) { setPendingExpenses(await res.json()); setSelectedPending([]); }
    } catch { /* */ }
  }, [getAuthHeader, canApprove]);

  // Reset to page 1 whenever filters change (fixes "filter not refreshing" bug)
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, selLocations, selCategories, selSubcategories, selTypes, searchQuery, expModuleFilter]);

  useEffect(() => {
    if (hasAccess) fetchFormOptions();
    if (canViewRecords) { fetchExpenses(); fetchKpis(); }
    else setLoading(false);
    fetchPending();
  }, [fetchFormOptions, fetchExpenses, fetchKpis, hasAccess, canViewRecords, fetchPending]);

  // Client-side filtering for multi-select (since API only takes single values)
  const filtered = useMemo(() => {
    let data = [...expenses];
    if (selLocations.length > 1) data = data.filter(e => selLocations.includes(e.location));
    if (selCategories.length > 1) data = data.filter(e => selCategories.includes(e.category));
    if (selSubcategories.length > 1) data = data.filter(e => selSubcategories.includes(e.subcategory));
    if (selTypes.length > 1) data = data.filter(e => selTypes.includes(e.expense_type));
    // Sort
    data.sort((a, b) => {
      const av = a[sortField] || '';
      const bv = b[sortField] || '';
      if (sortField === 'amount') return sortDir === 'asc' ? (a.amount || 0) - (b.amount || 0) : (b.amount || 0) - (a.amount || 0);
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return data;
  }, [expenses, selLocations, selCategories, selSubcategories, selTypes, sortField, sortDir]);

  const totalFiltered = useMemo(() => filtered.reduce((s, e) => s + (e.amount || 0), 0), [filtered]);

  const filterOpts = useMemo(() => ({
    locations: [...new Set(expenses.map(e => e.location).filter(Boolean))].sort(),
    categories: [...new Set(expenses.map(e => e.category).filter(Boolean))].sort(),
    subcategories: [...new Set(expenses.map(e => e.subcategory).filter(Boolean))].sort(),
    types: ['Petty Cash', 'Monthly Expense', 'One-Time Expense'],
  }), [expenses]);

  const activeFilters = selLocations.length + selCategories.length + selSubcategories.length + selTypes.length + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  const toggle = (setter) => (val) => setter(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const clearFilters = () => { setSelLocations([]); setSelCategories([]); setSelSubcategories([]); setSelTypes([]); setDateFrom(''); setDateTo(''); setSearchQuery(''); setPage(1); };
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const categoryNames = useMemo(() => Object.keys(formOptions.categories || {}), [formOptions]);
  const subcatsForCategory = useMemo(() => (formOptions.subcategories_by_category || {})[form.category] || [], [formOptions, form.category]);
  const vendorsForCategory = useMemo(() => ((formOptions.vendors_by_category || {})[form.category] || []).map(v => typeof v === 'string' ? v : v.name), [formOptions, form.category]);


  const handleApprovalAction = async (action) => {
    if (selectedPending.length === 0) { toast.error('Select expenses to ' + action); return; }
    if (action === 'reject' && !rejectRemarks.trim()) { setRejectDialog(true); return; }
    setApprovalLoading(true);
    try {
      const res = await fetch(`${API}/expenses/approval-action`, {
        method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expense_ids: selectedPending, action, remarks: rejectRemarks }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setSelectedPending([]); setRejectRemarks(''); setRejectDialog(false);
        fetchPending(); fetchExpenses(); fetchKpis();
      } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); }
    finally { setApprovalLoading(false); }
  };

  const togglePendingSelect = (eid) => setSelectedPending(prev => prev.includes(eid) ? prev.filter(id => id !== eid) : [...prev, eid]);
  const selectAllPending = () => setSelectedPending(prev => prev.length === pendingExpenses.length ? [] : pendingExpenses.map(e => e.expense_id));

  const openAdd = () => {
    setEditingExpense(null);
    // Default location: pre-fill with user's profile location for convenience,
    // but the field remains editable so they can switch to any other site.
    const defaultLoc = formOptions.default_location || '';
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10), location: defaultLoc });
    setDialogOpen(true);
  };
  const openEdit = (exp) => { setEditingExpense(exp); setForm({ ...exp, attachment: exp.attachment || null }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.date || !form.location || !form.amount || !form.expense_type) {
      toast.error('Please fill required fields (Date, Location, Amount, Expense Type)'); return;
    }
    setSaving(true);
    try {
      let attachmentUrl = form.attachment;
      if (form.attachment instanceof File) {
        const fd = new FormData(); fd.append('file', form.attachment);
        const upRes = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
        if (upRes.ok) { const d = await upRes.json(); attachmentUrl = d.url || d.file_url; }
        else { toast.error('Upload failed'); setSaving(false); return; }
      }
      const body = {
        ...form,
        amount: parseFloat(form.amount),
        attachment: attachmentUrl,
        paid_by: form.paid_by || employee?.full_name || '',
        entity: form.entity || '',
        module: forcedExpModule || expModuleFilter || form.module || 'Admin',
      };
      delete body.expense_id; delete body.recorded_by; delete body.recorded_by_email; delete body.is_deleted; delete body.created_at; delete body.updated_at;
      const url = editingExpense ? `${API}/expenses/${editingExpense.expense_id}` : `${API}/expenses`;
      const method = editingExpense ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) { toast.success(editingExpense ? 'Updated' : 'Added'); setDialogOpen(false); fetchExpenses(); fetchKpis(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API}/expenses/${deleteConfirm}`, { method: 'DELETE', headers: getAuthHeader() });
      if (res.ok) { toast.success('Deleted'); fetchExpenses(); fetchKpis(); }
      else toast.error('Failed');
    } catch { toast.error('Failed'); }
    finally { setDeleteConfirm(null); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/expenses/bulk-upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      if (res.ok) { const d = await res.json(); toast.success(`${d.inserted} imported`); fetchExpenses(); fetchKpis(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    finally { setUploading(false); setBulkOpen(false); if (bulkFileRef.current) bulkFileRef.current.value = ''; }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await fetch(`${API}/expenses/export?${params}`, { headers: getAuthHeader() });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'Expenses.xlsx'; document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a); toast.success('Downloaded'); }
    } catch { toast.error('Export failed'); }
  };

  const handleTemplate = async () => {
    try {
      const res = await fetch(`${API}/expenses/template`, { headers: getAuthHeader() });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'Template.xlsx'; document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a); }
    } catch { toast.error('Failed'); }
  };

  const handleDownloadAttachment = async (attachmentUrl, expenseId) => {
    try {
      const res = await fetch(`${BACKEND_URL}${attachmentUrl}`, { headers: getAuthHeader() });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${expenseId}_invoice`; document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a); }
    } catch { toast.error('Download failed'); }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  if (!hasAccess) {
    return <Layout><div className="flex items-center justify-center min-h-[60vh]"><p className="text-muted-foreground">You don't have access to this page.</p></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="daily-expenses-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Daily Expenses</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Petty cash & expense management</p>
          </div>
          {canWrite && activeTab === 'records' && (
            <div className="flex gap-2 flex-wrap items-center">
              {/* Department slicer */}
              <div className="flex items-center gap-1 border rounded-md p-1 bg-card h-8" data-testid="expenses-module-slicer">
                {['Admin', 'IT'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    disabled={!!forcedExpModule && forcedExpModule !== opt}
                    onClick={() => setExpModuleFilter(opt)}
                    data-testid={`expenses-module-${opt.toLowerCase()}`}
                    className={`px-2.5 py-0.5 text-xs rounded font-medium transition ${
                      expModuleFilter === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    } ${(!!forcedExpModule && forcedExpModule !== opt) ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setBulkOpen(true)} data-testid="bulk-upload-btn"><Upload className="w-3.5 h-3.5 mr-1" />Bulk Upload</Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExport} data-testid="export-btn"><Download className="w-3.5 h-3.5 mr-1" />Export</Button>
              <Button size="sm" className="h-8 text-xs" onClick={openAdd} data-testid="add-expense-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Expense</Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-9">
            {canViewRecords && <TabsTrigger value="records" className="text-xs" data-testid="tab-records"><Receipt className="w-3.5 h-3.5 mr-1" />Records</TabsTrigger>}
            {canApprove && <TabsTrigger value="approvals" className="text-xs" data-testid="tab-approvals"><CheckSquare className="w-3.5 h-3.5 mr-1" />Approvals{pendingExpenses.length > 0 && <Badge variant="destructive" className="ml-1.5 px-1.5 py-0 text-[10px] h-4 rounded-full">{pendingExpenses.length}</Badge>}</TabsTrigger>}
            {canViewAnalytics && <TabsTrigger value="analytics" className="text-xs" data-testid="tab-analytics"><BarChart3 className="w-3.5 h-3.5 mr-1" />Analytics</TabsTrigger>}
          </TabsList>

          {canViewRecords && (
            <TabsContent value="records" className="mt-4">
              {/* KPI Cards */}
              {kpis && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-slate-400" /><span className="text-[10px] text-slate-300 uppercase tracking-wider font-medium">Total Expense</span></div>
                      <p className="text-xl font-bold" data-testid="kpi-total">{fmtCurrency(kpis.total_expenses)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-0">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4 text-emerald-200" /><span className="text-[10px] text-emerald-100 uppercase tracking-wider font-medium">This Month</span></div>
                      <p className="text-xl font-bold">{fmtCurrency(kpis.this_month_total)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1"><Receipt className="w-4 h-4 text-blue-200" /><span className="text-[10px] text-blue-100 uppercase tracking-wider font-medium">One-Time Expenses</span></div>
                      <p className="text-xl font-bold">{fmtCurrency(kpis.onetime_expense_total)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-600 to-violet-600 text-white border-0">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-purple-200" /><span className="text-[10px] text-purple-100 uppercase tracking-wider font-medium">Monthly Expenses</span></div>
                      <p className="text-xl font-bold">{fmtCurrency(kpis.monthly_expense_total)}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filters */}
              <Card className="bg-gray-50/80 dark:bg-zinc-900/50 border-gray-200 dark:border-zinc-800">
                <CardHeader className="pb-3 pt-4 px-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px] max-w-xs">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input placeholder="Search vendor, category, ticket ID..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} className="pl-8 h-8 text-xs" data-testid="expense-search" />
                    </div>
                    <MultiFilter label="Location" options={filterOpts.locations} selected={selLocations} onToggle={toggle(setSelLocations)} />
                    <MultiFilter label="Category" options={filterOpts.categories} selected={selCategories} onToggle={toggle(setSelCategories)} />
                    <MultiFilter label="Subcategory" options={filterOpts.subcategories} selected={selSubcategories} onToggle={toggle(setSelSubcategories)} />
                    <MultiFilter label="Expense Type" options={filterOpts.types} selected={selTypes} onToggle={toggle(setSelTypes)} />
                    <DateHierarchyFilter onDateChange={({ from, to }) => { setDateFrom(from || ''); setDateTo(to || ''); setPage(1); }} />
                    {activeFilters > 0 && <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearFilters}><X className="w-3 h-3 mr-1" />Clear</Button>}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
                      <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-medium text-sm">No expenses found for selected filters</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden md:block overflow-x-auto">
                        <Table>
                          <TableHeader className="sticky top-0 z-10">
                            <TableRow className="bg-gray-100 dark:bg-zinc-800 border-b border-gray-200 dark:border-zinc-700">
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('date')}>Date {sortField === 'date' && (sortDir === 'asc' ? '↑' : '↓')}</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Location</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('category')}>Category {sortField === 'category' && (sortDir === 'asc' ? '↑' : '↓')}</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Sub-Category</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Vendor</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('amount')}>Amount {sortField === 'amount' && (sortDir === 'asc' ? '↑' : '↓')}</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Type</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Entity</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Payment Mode</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Status</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Recorded By</TableHead>
                              <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground w-10"></TableHead>
                              <TableHead className="h-9 w-8"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filtered.map((exp, idx) => (
                              <TableRow key={exp.expense_id} className={`h-10 hover:bg-gray-100/80 dark:hover:bg-zinc-800/50 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-zinc-900' : 'bg-gray-50/60 dark:bg-zinc-800/20'}`}>
                                <TableCell className="py-2 text-[12px] font-medium whitespace-nowrap tabular-nums">{fmtDate(exp.date)}</TableCell>
                                <TableCell className="py-2 text-[12px] whitespace-nowrap">{exp.location}</TableCell>
                                <TableCell className="py-2 text-[12px] max-w-[140px] truncate" title={exp.category}>{exp.category || ''}</TableCell>
                                <TableCell className="py-2 text-[12px] max-w-[140px] truncate text-muted-foreground" title={exp.subcategory}>{exp.subcategory || ''}</TableCell>
                                <TableCell className="py-2 text-[12px] max-w-[100px] truncate text-muted-foreground" title={exp.vendor_name}>{exp.vendor_name || ''}</TableCell>
                                <TableCell className="py-2 text-[12px] text-right font-semibold whitespace-nowrap tabular-nums">{fmtCurrency(exp.amount)}</TableCell>
                                <TableCell className="py-2"><ExpenseTypeBadge type={exp.expense_type} /></TableCell>
                                <TableCell className="py-2 text-[11px] text-muted-foreground">{exp.entity || ''}</TableCell>
                                <TableCell className="py-2 text-[11px] text-muted-foreground">{exp.payment_mode || ''}</TableCell>
                                <TableCell className="py-2"><StatusBadge status={exp.status} /></TableCell>
                                <TableCell className="py-2 text-[11px] text-muted-foreground max-w-[120px] truncate" title={exp.paid_by || exp.recorded_by}>{(exp.paid_by || exp.recorded_by || '').split(' ').slice(0, 2).join(' ')}</TableCell>
                                <TableCell className="py-2">
                                  {exp.attachment && (
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" onClick={() => handleDownloadAttachment(exp.attachment, exp.expense_id)} title="Download Invoice" data-testid={`attach-${exp.expense_id}`}>
                                      <Paperclip className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell className="py-2">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" data-testid={`actions-${exp.expense_id}`}><MoreHorizontal className="w-3.5 h-3.5" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="min-w-[140px]">
                                      <DropdownMenuItem onClick={() => setViewDialog(exp)} className="text-xs"><Eye className="w-3.5 h-3.5 mr-2" />View Details</DropdownMenuItem>
                                      {canWrite && (!['Approved', 'Rejected'].includes(exp.status) || role === 'Super Admin') && <DropdownMenuItem onClick={() => openEdit(exp)} className="text-xs"><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>}
                                      {canDelete && <DropdownMenuItem className="text-destructive text-xs" onClick={() => setDeleteConfirm(exp.expense_id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden divide-y">
                        {filtered.map(exp => (
                          <div key={exp.expense_id} className="px-4 py-3 hover:bg-accent/30 active:bg-accent/50" onClick={() => setViewDialog(exp)}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="min-w-0 flex-1 mr-3">
                                <p className="text-xs font-medium">{fmtDate(exp.date)} &middot; {exp.location}</p>
                                <p className="text-[11px] text-muted-foreground truncate">{exp.category || 'Uncategorized'}{exp.subcategory ? ` / ${exp.subcategory}` : ''}</p>
                              </div>
                              <p className="text-sm font-semibold tabular-nums whitespace-nowrap">{fmtCurrency(exp.amount)}</p>
                            </div>
                            <ExpenseTypeBadge type={exp.expense_type} />
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                          <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({totalCount} total)</p>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {canApprove && (
            <TabsContent value="approvals" className="mt-4">
              <Card className="bg-gray-50/80 dark:bg-zinc-900/50 border-gray-200 dark:border-zinc-800">
                <CardHeader className="pb-3 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Pending Approvals</CardTitle>
                      <CardDescription>{pendingExpenses.length} expense(s) awaiting your approval</CardDescription>
                    </div>
                    {selectedPending.length > 0 && (
                      <div className="flex gap-2">
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprovalAction('approve')} disabled={approvalLoading} data-testid="bulk-approve-btn">
                          <CheckSquare className="w-3.5 h-3.5 mr-1" />Approve ({selectedPending.length})
                        </Button>
                        <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => setRejectDialog(true)} disabled={approvalLoading} data-testid="bulk-reject-btn">
                          <X className="w-3.5 h-3.5 mr-1" />Reject ({selectedPending.length})
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {pendingExpenses.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-medium text-sm">No pending approvals</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-100 dark:bg-zinc-800 border-b border-gray-200">
                            <TableHead className="h-9 w-10">
                              <Checkbox checked={selectedPending.length === pendingExpenses.length && pendingExpenses.length > 0} onCheckedChange={selectAllPending} className="h-3.5 w-3.5" />
                            </TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">ID</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Location</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sub-Category</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Vendor</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Amount</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Type</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Entity</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Recorded By</TableHead>
                            <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</TableHead>
                            <TableHead className="h-9 w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingExpenses.map((exp, idx) => (
                            <TableRow key={exp.expense_id} className={`h-10 hover:bg-accent/40 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-zinc-900' : 'bg-gray-50/60 dark:bg-zinc-800/20'}`}>
                              <TableCell className="py-2">
                                <Checkbox checked={selectedPending.includes(exp.expense_id)} onCheckedChange={() => togglePendingSelect(exp.expense_id)} className="h-3.5 w-3.5" data-testid={`select-${exp.expense_id}`} />
                              </TableCell>
                              <TableCell className="py-2 text-[11px] font-mono text-blue-600 cursor-pointer hover:underline" onClick={() => setViewDialog(exp)}>{exp.expense_id}</TableCell>
                              <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(exp.date)}</TableCell>
                              <TableCell className="py-2 text-[12px]">{exp.location}</TableCell>
                              <TableCell className="py-2 text-[12px] max-w-[120px] truncate" title={exp.category}>{exp.category}</TableCell>
                              <TableCell className="py-2 text-[12px] max-w-[120px] truncate text-muted-foreground" title={exp.subcategory}>{exp.subcategory}</TableCell>
                              <TableCell className="py-2 text-[12px] text-muted-foreground">{exp.vendor_name}</TableCell>
                              <TableCell className="py-2 text-[12px] text-right font-semibold tabular-nums">{fmtCurrency(exp.amount)}</TableCell>
                              <TableCell className="py-2"><ExpenseTypeBadge type={exp.expense_type} /></TableCell>
                              <TableCell className="py-2 text-[11px] text-muted-foreground">{exp.entity}</TableCell>
                              <TableCell className="py-2 text-[11px] text-muted-foreground" title={exp.recorded_by}>{(exp.recorded_by || '').split(' ').slice(0, 2).join(' ')}</TableCell>
                              <TableCell className="py-2 text-[11px] text-muted-foreground max-w-[120px] truncate" title={exp.description}>{exp.description}</TableCell>
                              <TableCell className="py-2">
                                {exp.attachment && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDownloadAttachment(exp.attachment, exp.expense_id)} title="Download Invoice"><Paperclip className="w-3.5 h-3.5" /></Button>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {canViewAnalytics && (
            <TabsContent value="analytics" className="mt-4">
              <AnalyticsView getAuthHeader={getAuthHeader} />
            </TabsContent>
          )}
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="h-9" data-testid="expense-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Expense Type *</Label>
                  <Select value={form.expense_type} onValueChange={v => setForm(p => ({ ...p, expense_type: v }))}>
                    <SelectTrigger className="h-9" data-testid="expense-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Petty Cash">Petty Cash</SelectItem>
                      <SelectItem value="Monthly Expense">Monthly Expense</SelectItem>
                      <SelectItem value="One-Time Expense">One-Time Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Location *</Label>
                  {formOptions.location_locked ? (
                    <Input value={form.location} readOnly className="h-9 bg-muted cursor-not-allowed" data-testid="expense-location" />
                  ) : (
                    <Select value={form.location} onValueChange={v => setForm(p => ({ ...p, location: v }))}>
                      <SelectTrigger className="h-9" data-testid="expense-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>{(formOptions.locations || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1"><Label className="text-xs">Amount *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" className="h-9" data-testid="expense-amount" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Entity</Label>
                  <Select value={form.entity} onValueChange={v => setForm(p => ({ ...p, entity: v }))}>
                    <SelectTrigger className="h-9" data-testid="expense-entity"><SelectValue placeholder="Select entity" /></SelectTrigger>
                    <SelectContent>{(formOptions.entities || ['UOSPL', 'UAFPL', 'UAPL']).map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={v => setForm(p => ({ ...p, payment_mode: v }))}>
                    <SelectTrigger className="h-9" data-testid="expense-payment-mode"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>{(formOptions.payment_sources || ['Petty Cash','DSR Amex Card','ISR Amex Card','DSR HDFC Card','ISR HDFC Card','Amrutha Gpay','DSR Gpay','UOSPL Accounts','UAPL Accounts','UAFPL Accounts']).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v, subcategory: '', vendor_name: '' }))}>
                    <SelectTrigger className="h-9" data-testid="expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Subcategory</Label>
                  <Select value={form.subcategory} onValueChange={v => setForm(p => ({ ...p, subcategory: v }))} disabled={!form.category}>
                    <SelectTrigger className="h-9" data-testid="expense-subcategory"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{subcatsForCategory.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Vendor</Label>
                  {vendorsForCategory.length > 0 ? (
                    <Select value={form.vendor_name} onValueChange={v => setForm(p => ({ ...p, vendor_name: v }))}>
                      <SelectTrigger className="h-9" data-testid="expense-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                      <SelectContent>{vendorsForCategory.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Input value={form.vendor_name} onChange={e => setForm(p => ({ ...p, vendor_name: e.target.value }))} placeholder="Vendor name" className="h-9" data-testid="expense-vendor" />}
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional" rows={2} className="text-sm" data-testid="expense-description" /></div>
              <div className="space-y-1"><Label className="text-xs">Attachment (Invoice)</Label>
                {form.attachment && typeof form.attachment === 'string' && <p className="text-[10px] text-muted-foreground">Current: {form.attachment.split('/').pop()}</p>}
                <Input type="file" ref={fileRef} onChange={e => { if (e.target.files?.[0]) setForm(p => ({ ...p, attachment: e.target.files[0] })); }} className="h-9" data-testid="expense-attachment" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-expense-btn">{saving ? 'Saving...' : editingExpense ? 'Update' : 'Add Expense'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={!!viewDialog} onOpenChange={() => setViewDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Expense Details</DialogTitle></DialogHeader>
            {viewDialog && (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground text-xs">ID</span><p className="font-mono text-xs">{viewDialog.expense_id}</p></div>
                  <div><span className="text-muted-foreground text-xs">Date</span><p>{fmtDate(viewDialog.date)}</p></div>
                  <div><span className="text-muted-foreground text-xs">Location</span><p>{viewDialog.location}</p></div>
                  <div><span className="text-muted-foreground text-xs">Amount</span><p className="font-bold">{fmtCurrency(viewDialog.amount)}</p></div>
                  <div><span className="text-muted-foreground text-xs">Category</span><p>{viewDialog.category || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Sub-Category</span><p>{viewDialog.subcategory || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Vendor</span><p>{viewDialog.vendor_name || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Expense Type</span><p><ExpenseTypeBadge type={viewDialog.expense_type} /></p></div>
                  <div><span className="text-muted-foreground text-xs">Entity</span><p>{viewDialog.entity || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Payment Mode</span><p>{viewDialog.payment_mode || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Paid By</span><p>{viewDialog.paid_by || viewDialog.recorded_by}</p></div>
                </div>
                {viewDialog.description && <div><span className="text-muted-foreground text-xs">Description</span><p>{viewDialog.description}</p></div>}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete Expense?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Permanently remove {deleteConfirm}?</p>
            <DialogFooter><Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button><Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Bulk Upload</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <p className="text-xs text-muted-foreground">Upload Excel with: Date, Location, Category, Subcategory, Amount, Vendor Name, Description</p>
              <Button variant="outline" size="sm" onClick={handleTemplate} data-testid="download-template-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Template</Button>
              <Input type="file" accept=".xlsx,.xls" ref={bulkFileRef} onChange={handleBulkUpload} disabled={uploading} data-testid="bulk-file-input" />
              {uploading && <p className="text-xs text-muted-foreground">Uploading...</p>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Reject Expense(s)</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <p className="text-xs text-muted-foreground">Rejecting {selectedPending.length} expense(s). Remarks are required.</p>
              <div className="space-y-1">
                <Label className="text-xs">Remarks *</Label>
                <Textarea value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} placeholder="Reason for rejection" rows={3} className="text-sm" data-testid="reject-remarks" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" disabled={!rejectRemarks.trim() || approvalLoading} onClick={() => handleApprovalAction('reject')} data-testid="confirm-reject-btn">
                {approvalLoading ? 'Rejecting...' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
